import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Building, Globe, Users } from 'lucide-react';

interface ProjectSetupStepProps {
  data?: any;
  onNext: (data?: any) => void;
  onBack: () => void;
  isFirst: boolean;
  isLast: boolean;
}

export function ProjectSetupStep({ data, onNext, onBack, isFirst, isLast }: ProjectSetupStepProps) {
  const [formData, setFormData] = useState({
    projectName: data?.projectName || '',
    website: data?.website || '',
    industry: data?.industry || '',
    teamSize: data?.teamSize || '',
    description: data?.description || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.projectName.trim()) {
      newErrors.projectName = 'Project name is required';
    }
    
    if (!formData.website.trim()) {
      newErrors.website = 'Website URL is required';
    } else if (!formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Please enter a valid URL (including http:// or https://)';
    }
    
    if (!formData.industry.trim()) {
      newErrors.industry = 'Industry is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext(formData);
    }
  };

  const industries = [
    'E-commerce',
    'SaaS/Technology',
    'Healthcare',
    'Education',
    'Finance',
    'Real Estate',
    'Travel & Hospitality',
    'Food & Beverage',
    'Fashion & Beauty',
    'Automotive',
    'Non-profit',
    'Other'
  ];

  const teamSizes = [
    '1-5 people',
    '6-20 people',
    '21-50 people',
    '51-200 people',
    '200+ people'
  ];

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-4">
          <Building className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Tell us about your project
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          This helps us customize your experience and provide better insights
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <Label htmlFor="projectName">Project Name *</Label>
          <Input
            id="projectName"
            value={formData.projectName}
            onChange={(e) => handleInputChange('projectName', e.target.value)}
            placeholder="Enter your project or company name"
            className={errors.projectName ? 'border-red-500' : ''}
          />
          {errors.projectName && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.projectName}</p>
          )}
        </div>

        <div>
          <Label htmlFor="website">Website URL *</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-3 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <Input
              id="website"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder="https://yourwebsite.com"
              className={`pl-10 ${errors.website ? 'border-red-500' : ''}`}
            />
          </div>
          {errors.website && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.website}</p>
          )}
        </div>

        <div>
          <Label htmlFor="industry">Industry *</Label>
          <select
            id="industry"
            value={formData.industry}
            onChange={(e) => handleInputChange('industry', e.target.value)}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.industry ? 'border-red-500' : ''
            }`}
          >
            <option value="">Select your industry</option>
            {industries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
          {errors.industry && (
            <p className="text-red-500 dark:text-red-400 text-sm mt-1">{errors.industry}</p>
          )}
        </div>

        <div>
          <Label htmlFor="teamSize">Team Size</Label>
          <div className="relative">
            <Users className="absolute left-3 top-3 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <select
              id="teamSize"
              value={formData.teamSize}
              onChange={(e) => handleInputChange('teamSize', e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select team size</option>
              {teamSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="description">Project Description (Optional)</Label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Tell us more about your project and goals..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            rows={3}
          />
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleNext}>
          Continue
        </Button>
      </div>
    </div>
  );
} 